import { Roles, User } from "@/redux/services/user/user.type";

// Shared access gates. router.tsx / page guards, Sidebar.tsx, ProtectedPage and
// PERMISSION_LANDINGS below must stay consistent; keep their permission strings
// here or next to these lists.

// The POS creates and edits orders, so it opens on orders.manage. Owners,
// managers, support and admin pass as full access; cashiers and waiters hold
// it through their default bundle (as do kitchen users, who are kept off the
// POS by the kitchen-only rule below).
export const POS_PERMISSIONS = ["orders.manage"];

// The floor hub ("Accueil") is the launchpad for order taking: its main action
// opens the POS, so it opens on the same permission (SFRESTO-165).
export const HUB_PERMISSIONS = POS_PERMISSIONS;

// The orders list (/orders) opens on orders.view (SFRESTO-184). Every
// functional role (cashier, waiter, kitchen) holds it by default; ROLE_STAFF
// needs an access role granting it.
export const ORDERS_VIEW_PERMISSIONS = ["orders.view"];

// Acting on an order from the orders list or its drawer (status changes,
// merge) needs orders.manage, like the POS edit it leads to (SFRESTO-184).
export const ORDERS_MANAGE_PERMISSIONS = ["orders.manage"];

// The menu editor (/menus/:id/edit) saves through menu.manage (SFRESTO-128).
export const MENU_EDIT_PERMISSIONS = ["menu.manage"];

// Paths a kitchen-only employee may always reach by functional role: the
// backend opens these to kitchen staff regardless of granular permissions.
// Exact-path match.
const KITCHEN_OPERATIONAL_PATHS = ["/kitchen"];

// Permission-gated sections a kitchen-only user may reach when they hold one of
// the listed permissions (the prefix itself or any sub-path). Mirrors the
// permission strings in Sidebar.tsx and the route guards. The POS is not listed:
// it stays closed to kitchen-only users.
const PERMISSION_GATED_PATHS: { prefix: string; permissions: string[] }[] = [
  { prefix: "/orders", permissions: ORDERS_VIEW_PERMISSIONS },
  { prefix: "/inventory", permissions: ["inventory.view", "suppliers.view"] },
  { prefix: "/expenses", permissions: ["expenses.view"] },
  { prefix: "/register", permissions: ["cash_register.view"] },
  { prefix: "/reports", permissions: ["reports.view"] },
  { prefix: "/customers", permissions: ["customers.view"] },
  { prefix: "/menus", permissions: ["menu.view"] },
  { prefix: "/dashboard", permissions: ["dashboard.view"] },
  { prefix: "/settings", permissions: ["settings.view"] },
];

// Kitchen staff with no other operational or full-access role.
export function isKitchenOnly(roles?: Roles[] | null): boolean {
  const list = roles ?? [];
  return (
    list.includes(Roles.kitchen) &&
    !list.some((r) =>
      [
        Roles.manager,
        Roles.owner,
        Roles.cashier,
        Roles.waiter,
        Roles.admin,
      ].includes(r)
    )
  );
}

// Whether a kitchen-only user may stay on `path` (ProtectedPage redirects them
// to /kitchen otherwise, and the Sidebar hides what they cannot reach).
// `canAny` is usePermissions().canAny.
export function kitchenOnlyMayReach(
  path: string,
  canAny: (...permissions: string[]) => boolean
): boolean {
  if (KITCHEN_OPERATIONAL_PATHS.includes(path)) return true;
  const gate = PERMISSION_GATED_PATHS.find(
    (g) => path === g.prefix || path.startsWith(`${g.prefix}/`)
  );
  return gate ? canAny(...gate.permissions) : false;
}

// Ordered landing candidates for permission-driven users: the first page whose
// view permission the user holds wins. Order roughly follows the sidebar.
const PERMISSION_LANDINGS: { permission: string; path: string }[] = [
  { permission: "dashboard.view", path: "/dashboard" },
  { permission: "orders.view", path: "/orders" },
  { permission: "cash_register.view", path: "/register" },
  { permission: "inventory.view", path: "/inventory" },
  { permission: "suppliers.view", path: "/inventory" },
  { permission: "expenses.view", path: "/expenses" },
  { permission: "reports.view", path: "/reports" },
  { permission: "customers.view", path: "/customers" },
  { permission: "menu.view", path: "/menus" },
  { permission: "settings.view", path: "/settings" },
];

// Decides where a user lands after login. Functional roles keep their home page
// (kitchen → kitchen, managers/owners → dashboard, cashier/waiter → the floor
// hub). Any
// other user (notably back-office ROLE_STAFF, whose access comes entirely from
// assigned access roles) is sent to the first page their permissions allow, and
// to /no-access if they can't view anything — never dumped onto /orders, which
// they may not be able to access.
export function getLandingPath(user?: User | null): string {
  const roles = user?.roles ?? [];

  if (isKitchenOnly(roles)) return "/kitchen";

  if (roles.includes(Roles.owner) || roles.includes(Roles.manager)) {
    return "/dashboard";
  }

  if (roles.includes(Roles.cashier) || roles.includes(Roles.waiter)) {
    return "/hub";
  }

  const permissions = new Set(user?.effectivePermissions ?? []);
  const landing = PERMISSION_LANDINGS.find((c) => permissions.has(c.permission));
  return landing?.path ?? "/no-access";
}

// Tab ids of the Menus page (/menus?tab=<id>).
export const MENUS_TABS = [
  "menu",
  "product",
  "daily_dish",
  "formula",
  "option",
  "table",
] as const;

export type MenusTab = (typeof MENUS_TABS)[number];

export function parseMenusTab(value: string | null): MenusTab | null {
  return (MENUS_TABS as readonly string[]).includes(value ?? "")
    ? (value as MenusTab)
    : null;
}

// Where a dish search result leads: the Plats tab filtered on the dish, with
// the dish's edit drawer opened for users who can edit it (SFRESTO-149).
export function getDishPath(dish: { id: string; title: string }): string {
  const params = new URLSearchParams({
    tab: "product",
    q: dish.title,
    dish: dish.id,
  });
  return `/menus?${params.toString()}`;
}

// Where the other menu search results lead (SFRESTO-167). Sections open their
// menu, options and option values open the option's details on the Options
// tab. Hits indexed before the API added the parent id (SFAPI-278) fall back
// to the tab.
export function getSectionPath(section: { menu?: string }): string {
  return section.menu ? `/menus/${section.menu}` : "/menus";
}

export function getOptionPath(option: { id: string }): string {
  const params = new URLSearchParams({ tab: "option", option: option.id });
  return `/menus?${params.toString()}`;
}

export function getOptionValuePath(value: { option?: string }): string {
  return value.option ? getOptionPath({ id: value.option }) : "/menus?tab=option";
}
